import {ChangeDetectorRef, Component, ElementRef, OnInit, signal, ViewChild, ViewEncapsulation} from '@angular/core';
import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout';
import {animate, state, style, transition, trigger,} from '@angular/animations';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
  Scroll
} from '@angular/router';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {AccountService} from './core/services/account.service';
import {User, UserService} from './core/services/user.service';
import {ObjectType, RecentItem} from './shared/models/recently-accessed.model';
import {QuickViewService} from './shared/components/quick-view/quick-view.service';
import {combineLatest, defer, merge, of} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, mapTo, switchMap, take, tap} from 'rxjs/operators';
import {AnalyticsService} from './core/services/analytics.service';
import {AmplitudeService} from './core/services/amplitude.service';
import {LoaderService} from '@shared/services/loader.service';
import {SidenavService} from '@shared/services/sidenav.service';
import {PerformanceTrackerService} from './core/services/performance-tracker.service';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {MessageHandlerComponent} from './shared/components/message-handler/message-handler.component';
import {LoaderComponent} from './shared/components/loader/loader.component';
import {AppHeaderComponent} from './shared/components/app-header/app-header.component';
import {AppSidebarComponent} from './shared/components/app-sidebar/app-sidebar.component';
import {IdealClaimsDirective} from './shared/directives/ideal-claims.directive';
import {ActivitiesQuickViewComponent} from './shared/components/quick-view/quick-view.component';
import {MatSidenav, MatSidenavContainer} from '@angular/material/sidenav';
import {CdkScrollableModule} from '@angular/cdk/scrolling';
import {SlidePanelHostComponent} from '@shared/slide-panel/slide-panel-host.component';
import {SlidePanelService} from '@shared/slide-panel/slide-panel.service';
import {SessionTimeoutService} from './core/auth/session-timeout.service';

export interface LayoutState {
  isIconView: boolean;
  isMobileView: boolean;
  isMobileMenuOpen: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [`
    .quickView {
      box-sizing: border-box;
      overflow-x: hidden;
      display: flex;
      flex-flow: wrap;

      .mat-body {
        width: 100%;
        overflow-x: hidden;
        height: 100%;
      }

    }

    .quick-view-container {
      width: 415px;

      .mat-expansion-panel-body {
        padding: 0;
      }
    }

    .quick-view-padding {
      padding-right: 420px;
    }

    .mat-body {
      margin: var(--spacing-lg);
    }
  `],
  animations: [
    trigger('toggleAnimation', [
      state('collapsed', style({width: '50px', height: '100%'})),
      state('expanded', style({width: '175px', height: '100%'})),
      transition('* => *', [animate('0.1s')]),
    ]),
    trigger('mobileToggleAnimation', [
      state('open', style({height: '100%', width: '100%'})),
      state('closed', style({height: '50px', width: '100%'})),
      transition('* => *', [animate('.1s cubic-bezier(0.4,0,0.2,1)')]),
    ]),
  ],
  imports: [
    MessageHandlerComponent,
    LoaderComponent,
    NgClass,
    AppHeaderComponent,
    AppSidebarComponent,
    CdkScrollableModule,
    RouterOutlet,
    IdealClaimsDirective,
    ActivitiesQuickViewComponent,
    MatSidenavContainer,
    MatSidenav,
    NgTemplateOutlet,
    AsyncPipe,
    SlidePanelHostComponent,
  ]
})
export class AppComponent implements OnInit {
  @ViewChild('content')
  public content!: ElementRef<HTMLElement>;

  public layoutState = signal<LayoutState>({
    isIconView: false,
    isMobileMenuOpen: false,
    isMobileView: false,
  });

  public isDarkTheme = false;
  public routeData = {
    showHeader: false,
    showSidebar: false,
    showFooter: false,
  };

  public user!: User;
  public matomoSafeHtml?: SafeHtml;
  public quickViewReady$ = this.quickViewService.viewReady$;
  public quickViewModel$ = this.quickViewService.model$;
  public addPaddingRight$ = combineLatest([this.quickViewReady$, this.quickViewModel$])
    .pipe(
      map(([ready, model]) => ready && model && !model.overlay)
    );
  /** Padding for main content when legacy quick view is open only. Slide panel overlays content (no push). */
  public contentPaddingRight$ = this.addPaddingRight$.pipe(
    map((qvOpen) => (qvOpen ? '420px' : null))
  );

  public constructor(
    public breakpointObserver: BreakpointObserver,
    private account: AccountService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private domSanitizer: DomSanitizer,
    private changeDetector: ChangeDetectorRef,
    private userService: UserService,
    public quickViewService: QuickViewService,
    public slidePanelService: SlidePanelService,
    private analyticsService: AnalyticsService,
    private amplitudeService: AmplitudeService,
    public loaderService: LoaderService,
    public sidenavService: SidenavService,
    private performanceTrackerService: PerformanceTrackerService,
    private sessionTimeoutService: SessionTimeoutService,
  ) {
    this.performanceTrackerService.enableTracking(true);
  }

  public ngOnInit() {
    this.breakpointObserver
      .observe(['(max-width: 768px)'])
      .subscribe((breakpoint: BreakpointState) => {
        this.layoutState.set({
          ...this.layoutState(),
          isMobileView: breakpoint.matches,
          isIconView: breakpoint.matches
            ? false
            : this.layoutState().isIconView,
        });
      });

    const matomo$ = this.analyticsService.getMatomo().pipe(
      take(1),
      tap((rawHtml) => this.buildMatomoScripts(rawHtml)),
      catchError(e => of(true)), // TODO: handle error
    );

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loaderService.load();
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.loaderService.complete();
      }
    });

    combineLatest([
      this.router.events,
      this.userService.user$.pipe(
        switchMap(u => matomo$.pipe(mapTo(u)))
      )
    ]).subscribe(([event, user]) => {
      if (!this.user) {
        this.user = user;
        this.amplitudeService.identify(user);
        this.routeData = {showHeader: true, showSidebar: true} as any;
        this.sessionTimeoutService.start();
        this.changeDetector.detectChanges();
      }

      if (event instanceof NavigationEnd) {
        this.amplitudeService.trackPageView(event.urlAfterRedirects);
        if (this.activatedRoute.firstChild) {
          this.routeData = this.activatedRoute.firstChild.firstChild?.snapshot.data as any;
        }
        // TODO: eliminate this workaround. Actual issue: not able to read route data when using resolve on route.
        else if (event.url.toLowerCase().indexOf('/deals/deal') > -1) {
          this.routeData = {showHeader: true, showSidebar: true} as any;
        }
        this.quickViewService.close();
      }

      if (event instanceof Scroll) {
        this.resetContentScrollPosition();
      }
    });

    // Record recently-accessed items. The URL stream is seeded with the router's *current* URL
    // (via defer) so a cold load / new-tab deep link is captured even when the initial
    // NavigationEnd already fired before this subscription was established — the exact scenario
    // when a property is opened in a new tab (target="_blank") from the company profile's
    // Properties section. Subsequent in-app navigations arrive through router.events.
    // We wait for the first user emission (take(1)) so this.user.id is available when recording,
    // and distinctUntilChanged avoids duplicate writes for the same URL.
    this.userService.user$
      .pipe(
        take(1),
        switchMap(user => {
          if (!this.user) {
            this.user = user;
          }
          return merge(
            defer(() => of(this.router.url)),
            this.router.events.pipe(
              filter((e): e is NavigationEnd => e instanceof NavigationEnd),
              map(e => e.urlAfterRedirects),
            ),
          );
        }),
        distinctUntilChanged(),
      )
      .subscribe(url => this.addEntityToRecentItems(url));
  }

  public addEntityToRecentItems(url: string) {
    const urlElements = url.split('/');
    const section = urlElements[1]?.toLowerCase();

    let itemType: ObjectType | null = null;
    let idSegment: string | undefined;

    if (section === 'properties') {
      // /properties/{id}
      itemType = ObjectType.Property;
      idSegment = urlElements[2];
    } else if (section === 'deals') {
      // Deal detail routes: /deals/{id}, /deals/is/{id}, /deals/debt/{id} (optionally
      // followed by sub-routes such as /quotes). The legacy /deals/deal/{id} shape redirects
      // to a platform route, so handle it too.
      itemType = ObjectType.Deal;
      const second = urlElements[2]?.toLowerCase();
      idSegment = (second === 'is' || second === 'debt' || second === 'deal')
        ? urlElements[3]
        : urlElements[2];
    } else {
      // /{plural}/{singular}/{id} (entity name at index 2, id at index 3).
      const entityRoute = urlElements[2]?.toLowerCase();
      switch (entityRoute) {
        case 'contact':
          itemType = ObjectType.Contact;
          break;
        case 'company':
          itemType = ObjectType.Company;
          break;
        default:
          break;
      }
      idSegment = urlElements[3];
    }

    if (itemType !== null) {
      // parseInt tolerates trailing query/matrix params (e.g. "5?tab=deals").
      const parsedId = parseInt(idSegment ?? '', 10);
      const entityId = Number.isNaN(parsedId) ? 0 : parsedId;

      // Skip list/landing pages that carry no entity id (e.g. /properties, /deals).
      if (entityId > 0 && this.user?.id != null) {
        const payload: RecentItem = {
          objectId: entityId,
          itemType,
          userId: this.user.id,
          lastVisited: new Date(),
        };
        this.userService.addRecentItem(payload).subscribe({
          error: err => console.error('Failed to record recent item', err),
        });
      }
    }
    this.sidenavService.close();
    this.sidenavService.isOpenSideBar.next(false);
  }

  public toggleTheme(isDarkTheme: boolean) {
    this.isDarkTheme = isDarkTheme;
    this.changeDetector.detectChanges();
  }

  public toggleNavigation() {
    this.layoutState.set({
      ...this.layoutState(),
      isIconView: !this.layoutState().isIconView,
    });
    this.changeDetector.detectChanges();
  }

  public toggleMobileNavigation() {
    this.layoutState.set({
      ...this.layoutState(),
      isMobileMenuOpen: !this.layoutState().isMobileMenuOpen,
    });

    this.changeDetector.detectChanges();
  }

  public async logout(): Promise<any> {
    this.sessionTimeoutService.stop();
    this.amplitudeService.reset();
    this.account.logout();
  }


  private resetContentScrollPosition() {
    setTimeout(() => {
      if (this.content?.nativeElement) {
        this.content.nativeElement?.scrollIntoView({block: 'start'});
      }
    });
  }

  private buildMatomoScripts(rawHtml: string) {
    this.matomoSafeHtml = this.domSanitizer.bypassSecurityTrustHtml(rawHtml);
    const placeholder = document.createElement('div');
    placeholder.id = 'matomo-analytics';
    placeholder.innerHTML = this.matomoSafeHtml as string;
    document.head.appendChild(placeholder);

    let counter = 1;
    let before = document.head.childNodes[0] as HTMLElement;
    placeholder.childNodes.forEach((c, i) => {
      const el = c as Element;
      if (el.tagName !== 'SCRIPT') {
        return;
      }
      const s = document.createElement('script');
      s.id = `matomo-analytics-${counter++}`;
      s.innerHTML = el.innerHTML;
      el.remove();
      before.before(s);
      before = s;
    });

    this.matomoSafeHtml = undefined;
    placeholder.remove();
  }
}
